import type { VcnCardBulkItem } from '../../api/dto/vcn-card-bulk.dto'
import type { VcnProviderFilter } from './types'
import { cell, normalizeRowKeys } from './VcnManagementBulkUploadModal.helpers'

/**
 * The template an admin downloads. Two columns, because those are the only two
 * the action uses — the provider is not decoration: token uniqueness is
 * per-provider, so a token without one does not identify a card.
 */
export const CARD_BULK_TEMPLATE_CSV = ['provider,vcnToken'].join('\n')

/**
 * Header spellings accepted for each column.
 *
 * `vcn token` with a space is the one that matters: it is what the Card Mode
 * export writes, and accepting it is what lets an exported file be uploaded
 * without being reshaped first. Headers are lowercased and trimmed before
 * matching, and any column not named here is ignored — so the export's other
 * eight columns ride along harmlessly.
 */
const PROVIDER_ALIASES = ['provider', 'vcn provider', 'vcnprovider'] as const
// 'token id' is what the Card Mode export writes since the PM's rename;
// the older spellings stay so a file downloaded before it still uploads.
const TOKEN_ALIASES = [
  'token id',
  'tokenid',
  'token_id',
  'vcntoken',
  'vcn_token',
  'vcn token',
  'token',
] as const

export interface ParsedCardBulkRows {
  items: VcnCardBulkItem[]
  /** 1-based spreadsheet line numbers, so an admin can find the row. */
  missingTokenLines: number[]
  badProviderLines: number[]
  /** The same card named twice; dropped rather than sent, since EPS rejects duplicates. */
  duplicateCount: number
}

/** Empty or unrecognised → null. Unlike the benefit upload there is no default. */
function parseCardProvider(raw: string): VcnProviderFilter | null {
  const value = raw.trim().toUpperCase()
  if (value === 'WEX') return 'WEX'
  if (value === 'MARQETA') return 'MARQETA'
  return null
}

/** Provider AND token, matching how EPS scopes token uniqueness. */
function itemKey(item: VcnCardBulkItem): string {
  return `${item.provider}:${item.vcnToken}`
}

/**
 * Reads `{provider, vcnToken}` rows out of a parsed CSV.
 *
 * Deliberately tolerant of extra columns and of header spelling, so the file an
 * admin exported from Card Mode uploads as-is. Deliberately intolerant of a
 * missing or unknown provider: guessing one would resolve a token in the wrong
 * namespace, and the cards it could match are other people's.
 */
export function parseCardBulkRows(
  data: Record<string, string>[]
): ParsedCardBulkRows {
  const items: VcnCardBulkItem[] = []
  const missingTokenLines: number[] = []
  const badProviderLines: number[] = []
  const seen = new Set<string>()
  let duplicateCount = 0

  data.forEach((raw, index) => {
    // +2: one for the header row, one because spreadsheets count from 1.
    const line = index + 2
    const norm = normalizeRowKeys(raw)
    const vcnToken = cell(norm, ...TOKEN_ALIASES)
    if (!vcnToken) {
      missingTokenLines.push(line)
      return
    }
    const provider = parseCardProvider(cell(norm, ...PROVIDER_ALIASES))
    if (provider === null) {
      badProviderLines.push(line)
      return
    }
    const item: VcnCardBulkItem = { provider, vcnToken }
    const key = itemKey(item)
    if (seen.has(key)) {
      // Sending it twice would terminate the card, then fail against a closed
      // card — which reads as a broken job rather than a duplicated row.
      duplicateCount += 1
      return
    }
    seen.add(key)
    items.push(item)
  })

  return { items, missingTokenLines, badProviderLines, duplicateCount }
}
